/**
 * useLinkValidityChoice
 * The validity an admin picks for an external user's link while delegating.
 * Expiry ends access, so the end date is pre-filled from the tasks
 * (`suggestLinkValidity`: latest due date + 14 days):
 * - no active link (none, or an expired one the delegation replaces): the
 *   suggestion, always sent; without one the admin must choose (`complete`
 *   stays false until then);
 * - an active expiring link that ends before the suggestion: the extension is
 *   pre-filled and sent (`extendsFrom`) unless the admin resets it;
 * - otherwise the active link's validity is shown and sent only once changed.
 */
import { useState, useEffect } from 'react';
import { LinkValidity } from '../api/externalLinksApi';
import { GuestLinkSummary, GuestLinkValidityPayload } from '../types/guestAccess';
import { isActiveLink, isValidityComplete, linkValidityPayload, validityOf, ValiditySuggestion } from '../utils/linkValidity';

export interface LinkValidityChoice {
    /** The user's current link as loaded (any state), for the summary line */
    current: GuestLinkSummary | null;
    /** The user's active link, or null when a new one will be minted */
    existing: GuestLinkSummary | null;
    /** The control's value; null while a new link has no validity chosen */
    value: LinkValidity | null;
    setValue: (value: LinkValidity | null) => void;
    /** The suggestion the pre-filled value comes from, if any */
    suggestion: ValiditySuggestion | null;
    /** Set while the value is a pre-filled extension of the active link: its current end date */
    extendsFrom: string | null;
    /** Fields to send with the delegation */
    payload: GuestLinkValidityPayload;
    /** False until a usable validity is chosen; the host disables its submit */
    complete: boolean;
}

/**
 * @param link       The selected user's current link (undefined while loading)
 * @param resetKey   Resets the choice when it changes (the selected user's uid)
 * @param suggestion From suggestLinkValidity over the user's tasks and this one
 */
export function useLinkValidityChoice(
    link: GuestLinkSummary | null | undefined,
    resetKey: string | null,
    suggestion: ValiditySuggestion | null = null
): LinkValidityChoice {
    const [chosen, setChosen] = useState<LinkValidity | null>(null);

    useEffect(() => {
        setChosen(null);
    }, [resetKey]);

    const existing = isActiveLink(link) ? link : null;
    const suggested: LinkValidity | null = suggestion ? { kind: 'expiring', expiresAt: suggestion.expiresAt } : null;
    const extension = existing && existing.kind === 'expiring' && existing.expiresAt && suggestion
        && existing.expiresAt < suggestion.expiresAt ? suggested : null;

    // What is sent: the admin's choice, else the pre-fill (new link or extension), else nothing.
    const sent = chosen ?? (existing ? extension : suggested);
    const value = sent ?? (existing ? validityOf(existing) : null);

    return {
        current: link ?? null,
        existing,
        value,
        setValue: setChosen,
        suggestion,
        extendsFrom: !chosen && extension && existing ? existing.expiresAt ?? null : null,
        payload: linkValidityPayload(sent),
        complete: value !== null && isValidityComplete(value),
    };
}
